import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
// import LoginImg from "https://res.cloudinary.com/dmrlotfsv/image/upload/t_VESHH-EON_WEBSITE/Login_xyok0l.png";
import { loginUser, clearError } from "../../redux/slices/authSlice";
import { mergeCart } from '../../redux/slices/cartSlice';

// ─── Font ─────────────────────────────────────────────────────────────────────
if (typeof document !== 'undefined' && !document.getElementById('veshh-font')) {
  const link = document.createElement('link');
  link.id = 'veshh-font'; link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Exo+2:wght@200;300;400;500;600&display=swap';
  document.head.appendChild(link);
}

const Login = () => {
  const [email,        setEmail]        = useState('');
  const [password,     setPassword]     = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showError,    setShowError]    = useState(false);

  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const location  = useLocation();

  const { user, guestId, loading, error, deletedAccountInfo } = useSelector((state) => state.auth);
  const { cart }                                               = useSelector((state) => state.cart);

  const redirect           = new URLSearchParams(location.search).get('redirect') || '/';
  const isCheckoutRedirect = redirect.includes('checkout');

  // Show / auto-dismiss error
  useEffect(() => {
    if (error) {
      setShowError(true);
      const t = setTimeout(() => { setShowError(false); dispatch(clearError()); }, 5000);
      return () => clearTimeout(t);
    }
  }, [error, dispatch]);

  // Redirect after login
  useEffect(() => {
    if (user) {
      if (cart?.products?.length > 0 && guestId) {
        dispatch(mergeCart({ guestId, user })).then(() => {
          navigate(isCheckoutRedirect ? '/checkout' : '/');
        });
      } else {
        navigate(isCheckoutRedirect ? '/checkout' : '/');
      }
    }
  }, [user, guestId, cart, navigate, isCheckoutRedirect, dispatch]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowError(false);
    dispatch(loginUser({ email, password }));
  };

  const dismissError = () => { setShowError(false); dispatch(clearError()); };

  const errorMessage = !error ? '' :
    error === 'Invalid credentials' || error === 'Invalid email or password'
      ? 'Invalid email or password. Please try again.'
      : error === 'User not found'
      ? 'No account found with this email address.'
      : error || 'Login failed. Please try again.';

  return (
    <>
      <style>{loginStyles}</style>

      <div className="lg-root">

        {/* ── LEFT — form ── */}
        <div className="lg-left">
          <div className="lg-form-wrap">

            {/* Logo */}
            <div className="lg-logo-wrap">
              <img
                src="https://res.cloudinary.com/dmrlotfsv/image/upload/v1754071341/VESHH-EON_TYPE_WEB_ppfyxy.png"
                alt="VESHH-EON"
                className="lg-logo"
              />
            </div>

            {/* Header */}
            <div className="lg-heading-wrap">
              <span className="lg-eyebrow">Your Account</span>
              <h2 className="lg-heading">
                Welcome <span className="lg-heading-bold">Back</span>
              </h2>
            </div>

            <div className="lg-rule"><hr className="lg-hr" /></div>

            {/* Error */}
            {showError && error && (
              <div className="lg-error" style={{ animation: 'lg-shake 0.45s ease' }}>
                <span className="lg-error-text">{errorMessage}</span>
                <button className="lg-error-close" onClick={dismissError}>✕</button>
              </div>
            )}

            {/* Deleted account — recovery prompt */}
            {deletedAccountInfo && (
              <div className="lg-recover">
                <div className="lg-recover-text">
                  <p className="lg-recover-head">Account pending deletion</p>
                  <p className="lg-recover-sub">
                    {deletedAccountInfo.scheduledDeletionAt
                      ? `Permanent deletion scheduled for ${new Date(deletedAccountInfo.scheduledDeletionAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}.`
                      : 'Your account has been deactivated.'}
                    {' '}You can restore it within the 30-day window.
                  </p>
                </div>
                <Link to="/recover-account" className="lg-recover-link">
                  Recover Account →
                </Link>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="lg-form">

              <div className="lg-field">
                <label className="lg-label" htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@veshheon.bharat"
                  required
                  className={`lg-input ${showError && error ? 'lg-input--err' : ''}`}
                />
              </div>

              <div className="lg-field">
                <div className="lg-label-row">
                  <label className="lg-label" htmlFor="password">Password</label>
                  <Link to="/forgot-password" className="lg-forgot">
                    Forgot password?
                  </Link>
                </div>
                <div className="lg-password-wrap">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Your password"
                    required
                    className={`lg-input lg-input--password ${showError && error ? 'lg-input--err' : ''}`}
                  />
                  <button
                    type="button"
                    className="lg-eye-btn"
                    onClick={() => setShowPassword(p => !p)}
                    tabIndex={-1}
                  >
                    {showPassword
                      ? <EyeOff size={15} strokeWidth={1.75} />
                      : <Eye    size={15} strokeWidth={1.75} />
                    }
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="lg-submit"
              >
                {loading ? (
                  <span className="lg-submit-loading">
                    <Loader2 size={13} className="lg-spinner" />
                    Signing In...
                  </span>
                ) : (
                  'Sign In'
                )}
              </button>

            </form>

            {/* Register */}
            <p className="lg-register">
              Don't have an account?{' '}
              <Link
                to={`/register?redirect=${encodeURIComponent(redirect)}`}
                className="lg-register-link"
              >
                Create Account
              </Link>
            </p>

            {/* Legal */}
            <p className="lg-legal">
              By signing in you agree to our{' '}
              <Link to="/terms" className="lg-legal-link">Terms</Link>
              {' '}and{' '}
              <Link to="/privacypolicy" className="lg-legal-link">Privacy Policy</Link>
            </p>

          </div>
        </div>

        {/* ── RIGHT — image ── */}
        <div className="lg-right">
          <div className="lg-right-overlay" />
         <img 
  src="https://res.cloudinary.com/dmrlotfsv/image/upload/t_VESHH-EON_WEBSITE/Login_xyok0l.png" 
  alt="VESHH-EON" 
  className="lg-right-img" 
/>
          <div className="lg-right-copy">
            <span className="lg-right-eyebrow">The Brand</span>
            <h3 className="lg-right-heading">
              Experience Fashion,<br />
              <span className="lg-right-bold">Redefined.</span>
            </h3>
            <p className="lg-right-sub">
              Walk like you own the damn street.
            </p>
          </div>
        </div>

      </div>
    </>
  );
};

const loginStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Exo+2:wght@200;300;400;500;600&display=swap');

  @keyframes lg-shake {
    0%, 100% { transform: translateX(0); }
    15%, 45%, 75% { transform: translateX(-5px); }
    30%, 60%, 90% { transform: translateX(5px); }
  }
  @keyframes lg-spin { to { transform: rotate(360deg); } }

  .lg-root {
    display: flex;
    min-height: 100vh;
    font-family: 'Exo 2', sans-serif;
  }

  /* ── LEFT ── */
  .lg-left {
    width: 100%;
    background: #f8f8f8;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px 24px;
  }
  @media (min-width: 768px) {
    .lg-left { width: 50%; padding: 64px 48px; }
  }

  .lg-form-wrap {
    width: 100%;
    max-width: 400px;
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  /* Logo */
  .lg-logo-wrap {
    display: flex;
    justify-content: center;
    margin-bottom: 36px;
  }
  .lg-logo {
    height: 36px;
    width: auto;
    object-fit: contain;
  }

  /* Heading */
  .lg-heading-wrap { margin-bottom: 24px; }

  .lg-eyebrow {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 8px;
    font-weight: 400;
    letter-spacing: 0.5em;
    text-transform: uppercase;
    color: black;
    margin-bottom: 10px;
  }
  .lg-eyebrow::before {
    content: '';
    width: 20px; height: 1px;
    background: rgba(0,0,0,0.2);
    display: block; flex-shrink: 0;
  }

  .lg-heading {
    font-size: clamp(28px, 4vw, 44px);
    font-weight: 200;
    color: #000;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    line-height: 1;
    margin: 0;
  }
  .lg-heading-bold { font-weight: 600; }

  /* Rule */
  .lg-rule { margin-bottom: 28px; }
  .lg-hr {
    border: none;
    border-top: 1px solid black;
    margin: 0;
  }

  /* Error */
  .lg-error {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 14px;
    border: 1px solid black;
    margin-bottom: 20px;
  }
  .lg-error-text {
    font-size: 9px;
    font-weight: 400;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: black;
    flex: 1;
  }
  .lg-error-close {
    background: transparent;
    border: none;
    cursor: pointer;
    font-size: 10px;
    color: black;
    padding: 0;
    transition: color 0.15s;
  }
  .lg-error-close:hover { color: #000; }

  /* Recover banner */
  .lg-recover {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 14px 16px;
    border: 1px solid #000;
    background: #000;
    margin-bottom: 20px;
  }
  .lg-recover-head {
    font-size: 8px; font-weight: 600; letter-spacing: 0.4em;
    text-transform: uppercase; color: rgba(255,255,255,0.6);
    margin: 0 0 4px;
  }
  .lg-recover-sub {
    font-size: 10px; font-weight: 300; letter-spacing: 0.04em;
    color: rgba(255,255,255,0.75); line-height: 1.6; margin: 0;
  }
  .lg-recover-link {
    display: inline-block;
    font-family: 'Exo 2', sans-serif;
    font-size: 8px; font-weight: 600; letter-spacing: 0.4em;
    text-transform: uppercase; color: #fff;
    text-decoration: none;
    border-bottom: 1px solid rgba(255,255,255,0.3);
    padding-bottom: 1px; align-self: flex-start;
    transition: border-color 0.2s;
  }
  .lg-recover-link:hover { border-color: #fff; }

  /* Form */
  .lg-form {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .lg-field {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .lg-label-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .lg-label {
    font-size: 8px;
    font-weight: 500;
    letter-spacing: 0.45em;
    text-transform: uppercase;
    color: black;
  }

  .lg-forgot {
    font-size: 8px;
    font-weight: 400;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: black;
    text-decoration: none;
    transition: color 0.2s;
  }
  .lg-forgot:hover { color: #000; }

  .lg-input {
    background: transparent;
    border: none;
    border-bottom: 1px solid black;
    outline: none;
    font-family: 'Exo 2', sans-serif;
    font-size: 13px;
    font-weight: 300;
    letter-spacing: 0.06em;
    color: #000;
    padding: 10px 0;
    width: 100%;
    transition: border-color 0.2s ease;
    border-radius: 0;
    -webkit-appearance: none;
  }
  .lg-input::placeholder { color: black; }
  .lg-input:focus { border-bottom-color: #000; }
  .lg-input--err { border-bottom-color: black !important; }
  .lg-input--password { padding-right: 28px; }

  .lg-password-wrap {
    position: relative;
    display: flex;
    align-items: center;
  }

  .lg-eye-btn {
    position: absolute;
    right: 0;
    background: transparent;
    border: none;
    cursor: pointer;
    color: black;
    display: flex;
    align-items: center;
    padding: 0;
    transition: color 0.15s;
  }
  .lg-eye-btn:hover { color: #000; }

  /* Submit */
  .lg-submit {
    width: 100%;
    background: #000;
    color: #fff;
    border: none;
    font-family: 'Exo 2', sans-serif;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.4em;
    text-transform: uppercase;
    padding: 16px;
    cursor: pointer;
    transition: background 0.2s ease;
    margin-top: 8px;
  }
  .lg-submit:hover:not(:disabled) { background: #222; }
  .lg-submit:disabled { background: black; cursor: not-allowed; }

  .lg-submit-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .lg-spinner { animation: lg-spin 1s linear infinite; }

  /* Register */
  .lg-register {
    margin-top: 24px;
    text-align: center;
    font-size: 10px;
    font-weight: 300;
    letter-spacing: 0.15em;
    color: black;
  }

  .lg-register-link {
    font-weight: 600;
    color: #000;
    text-decoration: none;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    font-size: 9px;
    position: relative;
    padding-bottom: 2px;
  }
  .lg-register-link::after {
    content: '';
    position: absolute;
    bottom: 0; left: 0;
    width: 100%; height: 1px;
    background: black;
    transition: background 0.2s;
  }
  .lg-register-link:hover::after { background: #000; }

  /* Legal */
  .lg-legal {
    margin-top: 16px;
    text-align: center;
    font-size: 8px;
    font-weight: 300;
    letter-spacing: 0.15em;
    color: black;
    text-transform: uppercase;
  }

  .lg-legal-link {
    color: black;
    text-decoration: underline;
    transition: color 0.2s;
  }
  .lg-legal-link:hover { color: #000; }

  /* ── RIGHT ── */
  .lg-right {
    display: none;
    position: relative;
    overflow: hidden;
    background: #000;
  }
  @media (min-width: 768px) {
    .lg-right { display: flex; width: 50%; }
  }

  .lg-right-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.15) 60%, transparent 100%);
    z-index: 1;
  }

  .lg-right-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .lg-right-copy {
    position: absolute;
    bottom: 48px;
    left: 48px;
    right: 48px;
    z-index: 2;
  }

  .lg-right-eyebrow {
    display: block;
    font-size: 8px;
    font-weight: 400;
    letter-spacing: 0.5em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.4);
    margin-bottom: 14px;
  }

  .lg-right-heading {
    font-size: clamp(24px, 3vw, 40px);
    font-weight: 200;
    color: #fff;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    line-height: 1.1;
    margin: 0 0 12px;
  }
  .lg-right-bold { font-weight: 600; }

  .lg-right-sub {
    font-size: 11px;
    font-weight: 300;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.4);
    margin: 0;
  }
`;

export default Login;
